import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./components/Sidebar";
import FrameSizeIcon from "./components/FrameSizeIcon";
import API_BASE_URL from "./config";
import "./PhotoFrameSettings.css";

function PhotoFrameSettings() {
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [prices, setPrices] = useState([]);
  const [shipFromState, setShipFromState] = useState(null);
  const [shipFromCountry, setShipFromCountry] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  const token = localStorage.getItem("token");
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const fetchSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/settings`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setEnabled(!!data.enabled);
      setPrices(data.prices || []);
      setShipFromState(data.shipFromState);
      setShipFromCountry(data.shipFromCountry);
    } catch {
      setMessage({ text: "Failed to load photo frame settings", type: "error" });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (message.text) {
      const t = setTimeout(() => setMessage({ text: "", type: "" }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const updatePrice = (frameDimensionId, field, value) => {
    setPrices((prev) => prev.map((p) => (p.frame_dimension_id === frameDimensionId ? { ...p, [field]: value } : p)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/photo-frame/settings`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          enabled,
          prices: prices.map((p) => ({
            frame_dimension_id: p.frame_dimension_id,
            price: p.price,
            shipping_within_state: p.shipping_within_state,
            shipping_outside_state: p.shipping_outside_state,
            shipping_international: p.shipping_international,
          })),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Save failed");
      setMessage({ text: "Saved!", type: "success" });
      fetchSettings();
    } catch (err) {
      setMessage({ text: err.message || "Failed to save settings", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const pricedCount = prices.filter((p) => p.price !== null && p.price !== "" && parseFloat(p.price) > 0).length;

  return (
    <div className="pf-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="pf-main">
        <header className="pf-header">
          <div className="pf-header-left">
            <button className="pf-toggle-sidebar" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <h1 className="pf-page-title">🖼️ Photo Frames</h1>
          </div>
        </header>

        <main className="pf-content">
          {message.text && <div className={`pf-message pf-message--${message.type}`}>{message.text}</div>}

          <div className="pf-info-card">
            <div className="pf-info-icon">🖼️</div>
            <div className="pf-info-text">
              <h3>Sell Physical Photo Frames</h3>
              <p>
                Once enabled, guests browsing your QR gallery can turn any photo into a framed print. Set a price
                for each size below — guests pay your price plus a platform markup, you get paid your full listed
                price. You fulfill orders yourself (print/frame + ship) from your Photo Frame Orders dashboard.
              </p>
            </div>
          </div>

          {loading ? (
            <p style={{ color: "#666" }}>Loading...</p>
          ) : (
            <>
              <div className="pf-section">
                <div className="pf-toggle-row">
                  <div>
                    <h3 className="pf-section-title">Photo Frame Shipping</h3>
                    <p className="pf-toggle-desc">
                      {enabled ? "Enabled — guests see the frame option in your QR gallery." : "Disabled — no frame option shown to guests."}
                    </p>
                  </div>
                  <label className="pf-switch">
                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    <span className="pf-switch-slider" />
                  </label>
                </div>
              </div>

              {!shipFromState && (
                <div className="pf-warning-banner">
                  ⚠️ Your profile doesn't have a state set yet, so we can't tell "within state" apart from "outside
                  state" for shipping. <a href="/profile">Add it in your Profile</a> — until then, every order uses
                  your outside-state rate.
                </div>
              )}

              <div className="pf-section">
                <h3 className="pf-section-title">Frame Prices</h3>
                <p className="pf-section-desc">
                  Leave a size blank to not offer it. {pricedCount === 0 && "You need at least one priced size to enable frame shipping."}
                </p>
                <div className="pf-price-grid">
                  {prices.map((p) => (
                    <div className="pf-price-card" key={p.frame_dimension_id}>
                      <FrameSizeIcon widthInches={p.width_inches} heightInches={p.height_inches} />
                      <div className="pf-price-label">{p.label}</div>
                      <div className="pf-price-dims">{p.width_inches}" × {p.height_inches}"</div>
                      <div className="pf-price-input-row">
                        <span className="pf-price-currency">₹</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="Not offered"
                          value={p.price ?? ""}
                          onChange={(e) => updatePrice(p.frame_dimension_id, "price", e.target.value)}
                        />
                      </div>

                      <div className="pf-shipping-block">
                        <div className="pf-shipping-label">Shipping cost</div>
                        <div className="pf-shipping-row">
                          <span className="pf-shipping-zone-label">Within {shipFromState || "your state"}</span>
                          <div className="pf-price-input-row pf-price-input-row--sm">
                            <span className="pf-price-currency">₹</span>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={p.shipping_within_state ?? ""}
                              onChange={(e) => updatePrice(p.frame_dimension_id, "shipping_within_state", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="pf-shipping-row">
                          <span className="pf-shipping-zone-label">Rest of {shipFromCountry || "India"}</span>
                          <div className="pf-price-input-row pf-price-input-row--sm">
                            <span className="pf-price-currency">₹</span>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={p.shipping_outside_state ?? ""}
                              onChange={(e) => updatePrice(p.frame_dimension_id, "shipping_outside_state", e.target.value)}
                            />
                          </div>
                        </div>
                        <div className="pf-shipping-row">
                          <span className="pf-shipping-zone-label">International</span>
                          <div className="pf-price-input-row pf-price-input-row--sm">
                            <span className="pf-price-currency">₹</span>
                            <input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={p.shipping_international ?? ""}
                              onChange={(e) => updatePrice(p.frame_dimension_id, "shipping_international", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {prices.length === 0 && <p style={{ color: "#666" }}>No frame sizes are available yet — check back soon.</p>}
              </div>

              <button className="pf-save-btn" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default PhotoFrameSettings;
